import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { listMenus } from '../../api/menu';
import { listWarehouses } from '../../api/orgData';
import { getEffectiveRecipeVersion, listRecipeLines, listRecipes } from '../../api/recipe';
import { createProductionBatch, listProductionBatches, postProductionBatch } from '../../api/production';
import { listItems } from '../../api/masterData';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import {
  Button,
  ClickableRow,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  FieldLabel,
  ListToolbar,
  LoadingNote,
  PageHeader,
  Select,
  StatusBadge,
  SummaryStrip,
  TextInput,
  formatDateTime,
  statusCounts,
  tableStyle,
  tdStyle,
  thStyle,
  usePagination,
  Pagination,
} from '../../components/common';
import type { Item, Menu, ProductionBatch, ProductionBatchStatus, Recipe, RecipeLine, Warehouse } from '../../types/domain';

const BATCH_STATUSES: ProductionBatchStatus[] = ['DRAFT', 'POSTED', 'CANCELLED'];

/**
 * Production (FASE 7, migration 0039). production_batches is branch +
 * warehouse scoped — re-fetches whenever "Cabang Aktif" changes, same
 * pattern as Goods Receipts. Posting consumes ingredient stock
 * (stock_ledger ISSUE rows); there is no finished-goods stock credit for
 * the produced menu — menus are not stock-tracked items in this schema
 * (see docs/data-dictionary.md NEEDS_DECISION log, 2026-08-19).
 *
 * STATUS: IMPLEMENTED_UNVERIFIED — see docs/data-dictionary.md NEEDS_DECISION log.
 */
export default function ProductionPage() {
  const { hasPermission } = usePermissions();
  const { activeBranchId, activeBranchWarehouseIds } = useBranch();
  const [batches, setBatches] = useState<ProductionBatch[]>([]);
  const [menus, setMenus] = useState<Menu[]>([]);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [selected, setSelected] = useState<ProductionBatch | null>(null);

  const canCreate = hasPermission('production_batch', 'create');
  const canPost = hasPermission('production_batch', 'post');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [batchRows, menuRows, recipeRows, whRows, itemRows] = await Promise.all([
        listProductionBatches(activeBranchId ?? undefined),
        listMenus(),
        listRecipes(),
        listWarehouses(activeBranchId ?? undefined),
        listItems(),
      ]);
      setBatches(batchRows);
      setMenus(menuRows);
      setRecipes(recipeRows);
      setWarehouses(whRows);
      setItems(itemRows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  // Branch-scoped — switching "Cabang Aktif" must re-fetch.
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId, activeBranchWarehouseIds.join(',')]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return batches.filter((b) => {
      if (q && !b.batch_number.toLowerCase().includes(q)) return false;
      if (statusFilter !== 'ALL' && b.status !== statusFilter) return false;
      return true;
    });
  }, [batches, search, statusFilter]);
  const { pageRows, page, pageCount, setPage, start, pageSize, total } = usePagination(filtered, 15);

  return (
    <div>
      <PageHeader title="Produksi" subtitle="Batch produksi — konsumsi bahan baku dari resep. Migrasi 0039." />
      {canCreate && (
        <Button variant="primary" onClick={() => setShowForm((v) => !v)} style={{ marginBottom: 16 }} disabled={!activeBranchId}>
          {showForm ? 'Batal' : '+ Batch Produksi'}
        </Button>
      )}
      {!activeBranchId && canCreate && <ErrorNote text="Belum ada branch aktif — tidak bisa membuat batch produksi." />}
      {showForm && canCreate && activeBranchId && (
        <NewBatchForm
          branchId={activeBranchId}
          menus={menus}
          recipes={recipes}
          warehouses={warehouses}
          onCreated={() => {
            setShowForm(false);
            void load();
          }}
        />
      )}
      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}
      {!loading && !error && (
        <>
          <SummaryStrip
            items={(() => {
              const c = statusCounts(batches, BATCH_STATUSES);
              return [
                { label: 'Total Batch', value: batches.length },
                { label: 'Draft', value: c.DRAFT, tone: 'neutral' as const },
                { label: 'Posted', value: c.POSTED, tone: 'success' as const },
                { label: 'Cancelled', value: c.CANCELLED, tone: 'danger' as const },
              ];
            })()}
          />
          <ListToolbar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Cari No. Batch..."
            resultCount={total}
            filters={
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 160 }}>
                <option value="ALL">Semua status</option>
                <option value="DRAFT">Draft</option>
                <option value="POSTED">Posted</option>
                <option value="CANCELLED">Cancelled</option>
              </Select>
            }
          />
          {batches.length === 0 && <EmptyState text="Belum ada batch produksi." />}
          {batches.length > 0 && total === 0 && <EmptyState text="Tidak ada batch yang cocok dengan pencarian/filter." />}
          {total > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>No. Batch</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Qty Produksi</th>
                  <th style={thStyle}>Dibuat</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((b) => (
                  <ClickableRow key={b.batch_id} onClick={() => setSelected(b)}>
                    <td style={tdStyle}>{b.batch_number}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={b.status} />
                    </td>
                    <td style={tdStyle}>{b.qty_produced}</td>
                    <td style={tdStyle}>{formatDateTime(b.created_at)}</td>
                    <td style={tdStyle}>
                      {canPost && b.status === 'DRAFT' && (
                        <Button
                          variant="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            postProductionBatch(b.batch_id).then(() => void load());
                          }}
                        >
                          Post
                        </Button>
                      )}
                    </td>
                  </ClickableRow>
                ))}
              </tbody>
            </table>
          )}
          <Pagination page={page} pageCount={pageCount} setPage={setPage} start={start} pageSize={pageSize} total={total} />
        </>
      )}
      <BatchDetailDrawer batch={selected} open={!!selected} onClose={() => setSelected(null)} items={items} canPost={canPost} onChanged={() => void load()} />
    </div>
  );
}

function NewBatchForm({
  branchId,
  menus,
  recipes,
  warehouses,
  onCreated,
}: {
  branchId: string;
  menus: Menu[];
  recipes: Recipe[];
  warehouses: Warehouse[];
  onCreated: () => void;
}) {
  const menusWithRecipe = useMemo(() => menus.filter((m) => recipes.some((r) => r.menu_id === m.menu_id)), [menus, recipes]);
  const [menuId, setMenuId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [qty, setQty] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const recipe = recipes.find((r) => r.menu_id === menuId);
    if (!recipe) {
      setError('Menu ini belum memiliki resep.');
      return;
    }
    const q = Number(qty);
    if (!Number.isFinite(q) || q <= 0) {
      setError('Qty produksi harus lebih dari 0.');
      return;
    }
    setSubmitting(true);
    try {
      const recipeVersionId = await getEffectiveRecipeVersion(recipe.recipe_id);
      if (!recipeVersionId) {
        setError('Resep menu ini belum memiliki versi yang berlaku.');
        setSubmitting(false);
        return;
      }
      await createProductionBatch(branchId, warehouseId, recipeVersionId, q);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginBottom: 20, padding: 16 }}>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <div style={{ flex: 2 }}>
          <FieldLabel label="Menu">
            <Select value={menuId} onChange={(e) => setMenuId(e.target.value)} required>
              <option value="" disabled>
                Pilih...
              </option>
              {menusWithRecipe.map((m) => (
                <option key={m.menu_id} value={m.menu_id}>
                  {m.name}
                </option>
              ))}
            </Select>
          </FieldLabel>
          {menusWithRecipe.length === 0 && (
            <div style={{ fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-text-faint)', marginTop: 4 }}>
              Belum ada menu dengan resep — buat resep di halaman Recipes / BOM terlebih dahulu.
            </div>
          )}
        </div>
        <div style={{ flex: 1 }}>
          <FieldLabel label="Gudang">
            <Select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)} required>
              <option value="" disabled>
                Pilih...
              </option>
              {warehouses.map((w) => (
                <option key={w.warehouse_id} value={w.warehouse_id}>
                  {w.name}
                </option>
              ))}
            </Select>
          </FieldLabel>
        </div>
        <div style={{ width: 100 }}>
          <FieldLabel label="Qty">
            <TextInput type="number" min="0.01" step="0.01" value={qty} onChange={(e) => setQty(e.target.value)} required />
          </FieldLabel>
        </div>
      </div>
      <Button type="submit" variant="primary" loading={submitting} disabled={menusWithRecipe.length === 0}>
        Buat Batch (Draft)
      </Button>
      {error && <ErrorNote text={error} />}
    </form>
  );
}

function BatchDetailDrawer({
  batch,
  open,
  onClose,
  items,
  canPost,
  onChanged,
}: {
  batch: ProductionBatch | null;
  open: boolean;
  onClose: () => void;
  items: Item[];
  canPost: boolean;
  onChanged: () => void;
}) {
  const [lines, setLines] = useState<RecipeLine[] | null>(null);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLines(null);
    if (!batch) return;
    listRecipeLines(batch.recipe_version_id)
      .then(setLines)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [batch]);

  if (!batch) return null;
  const itemName = (id: string) => items.find((i) => i.item_id === id)?.name ?? id;

  const handlePost = async () => {
    setPosting(true);
    setError(null);
    try {
      await postProductionBatch(batch.batch_id);
      onChanged();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPosting(false);
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title={batch.batch_number}>
      <DrawerField label="Status" value={<StatusBadge status={batch.status} />} />
      <DrawerField label="Qty Produksi" value={batch.qty_produced} />
      <DrawerField label="Dibuat" value={formatDateTime(batch.created_at)} />
      {batch.posted_at && <DrawerField label="Diposting" value={formatDateTime(batch.posted_at)} />}

      <div style={{ fontWeight: 700, fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-text-primary)', margin: '16px 0 8px' }}>
        Bahan yang Dikonsumsi
      </div>
      {lines === null && <LoadingNote />}
      {lines && lines.length > 0 && (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Bahan</th>
              <th style={thStyle}>Qty / Yield</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.recipe_line_id}>
                <td style={tdStyle}>{itemName(l.item_id)}</td>
                <td style={tdStyle}>
                  {l.qty_per_yield} × {batch.qty_produced}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {canPost && batch.status === 'DRAFT' && (
        <Button variant="primary" onClick={() => void handlePost()} loading={posting} style={{ marginTop: 16 }}>
          Post Batch
        </Button>
      )}
      {error && <ErrorNote text={error} />}
    </Drawer>
  );
}
